import {
  Button,
  Card,
  closeCurrentDialog,
  closeDialog,
  DataStatus,
  openDialog,
  TabProps,
  TabsHeader,
  Tooltip,
} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import {isNilOrEmpty, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {selectActiveBranchId} from '@dms/api/features';
import {
  PostSupplierOrderMaterialBasketItemRequest,
  useGetSupplierOrderQuery,
  useGetSupplierOrderVariantQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';
import {
  ArticleCreate,
  ArticleCreateDefaultValues,
  getSingleOrUndefined,
} from '@dms/shared-aftersales';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {suffixTestId, TestIdProps} from 'shared/web';

import {ExternalFilters} from 'features/eag-grid';

import {DEFAULT_TO_ORDER_QUANTITY} from '../constants/defaultToOrderQuantity';
import {MaterialPriceListTab} from './MaterialPriceListTab';
import {MaterialPurchaseListTab} from './MaterialPurchaseListTab';
import {MaterialWarehouseTab} from './MaterialWarehouseTab';

interface MaterialListProps extends TestIdProps {
  onAddMaterialToBasket: (
    articleId: PostSupplierOrderMaterialBasketItemRequest['body']['article']
  ) => Promise<void>;
  onAddMaterialFromPurchaseListToBasket: (requestItemId: string[]) => Promise<void>;
  hasPendingRequest: boolean;
}

const TABS_IDS = {
  warehouseTab: 'supplierOrderBasket-warehouseTab',
  purchaseListTab: 'supplierOrderBasket-purchaseListTab',
  priceListTab: 'supplierOrderBasket-priceListTab',
};

export function MaterialList(props: MaterialListProps) {
  const {supplierOrderId} = useWarehouseParams();
  const [activeTabId, setActiveTabId] = useState(TABS_IDS.warehouseTab);
  const [activeFolderId, setActiveFolderId] = useState<string>();
  const filters = useRef<ArticleCreateDefaultValues>({});

  const branchId = useSelector(selectActiveBranchId);

  const [articleCreate] = usePermissions({
    permissionKeys: ['articleCreate'],
  });

  const {data: supplierOrder, isLoading: isSupplierOrderLoading} = useGetSupplierOrderQuery(
    {orderId: supplierOrderId},
    {skip: isNilOrEmpty(supplierOrderId)}
  );

  const orderVariantId = supplierOrder?.order.orderVariantId ?? '';

  const {data: supplierOrderVariant, isLoading: isSupplierOrderVariantLoading} =
    useGetSupplierOrderVariantQuery({orderVariantId}, {skip: isNilOrEmpty(orderVariantId)});

  const warehouseId = supplierOrder?.order.warehouseId ?? '';
  const authorizationProfileId = supplierOrderVariant?.authorizationProfileId ?? '';

  const handleCreateArticle = () => {
    openDialog(
      <ArticleCreate
        isWarehouseDisabled
        defaultValues={{
          activeFolderId,
          catalogNumber: filters.current?.catalogNumber,
          manufacturerId: filters.current?.manufacturerId,
          name: filters.current?.name,
          warehouseId,
        }}
        onArticleCreate={(createdArticleData) => {
          props.onAddMaterialToBasket([
            {articleId: createdArticleData.articleId, toOrderQuantity: DEFAULT_TO_ORDER_QUANTITY},
          ]);
          closeCurrentDialog();
        }}
        submitButtonTitle={i18n.t('general.actions.createAndAdd')}
        onClose={() => closeDialog('createNewArticleDialog')}
        data-testid={suffixTestId('createNewArticle', props)}
      />,
      {
        id: 'createNewArticleDialog',
        title: i18n.t('general.labels.createNew'),
        scrollBehavior: 'outside',
        withAdditionalFooter: true,
      }
    );
  };

  const handleFiltersChange = (newFilters: ExternalFilters) => {
    const articleCreateDefaultValues: ArticleCreateDefaultValues = {};

    if (isString(newFilters.catalogNumber)) {
      articleCreateDefaultValues.catalogNumber = newFilters.catalogNumber;
    }

    if (isNotNilOrEmpty(newFilters.manufacturerId)) {
      articleCreateDefaultValues.manufacturerId = getSingleOrUndefined(newFilters.manufacturerId);
    }

    if (isNotNilOrEmpty(newFilters.warehouseId)) {
      articleCreateDefaultValues.warehouseId = getSingleOrUndefined(newFilters.warehouseId);
    }

    if (isString(newFilters.name)) {
      articleCreateDefaultValues.name = newFilters.name;
    }

    filters.current = articleCreateDefaultValues;
  };

  const tabs: TabProps[] = [
    {
      id: TABS_IDS.warehouseTab,
      title: i18n.t('entity.warehouse.labels.warehouse'),
      content: (
        <MaterialWarehouseTab
          orderId={supplierOrderId}
          warehouseId={warehouseId}
          onAddMaterialToBasket={props.onAddMaterialToBasket}
          onActiveFolderIdChange={setActiveFolderId}
          onExternalFilterChange={handleFiltersChange}
          hasPendingRequest={props.hasPendingRequest}
          data-testid={suffixTestId('tabs.warehouse', props)}
        />
      ),

      'data-testid': suffixTestId('tab.warehouse', props),
    },
    {
      id: TABS_IDS.priceListTab,
      title: i18n.t('entity.warehouse.labels.priceList'),
      content: (
        <MaterialPriceListTab
          warehouseId={warehouseId}
          branchId={branchId}
          authorizationProfileId={authorizationProfileId}
          onAddMaterialToBasket={props.onAddMaterialToBasket}
          hasPendingRequest={props.hasPendingRequest}
          data-testid={suffixTestId('tabs.priceList', props)}
        />
      ),
      'data-testid': suffixTestId('tab.priceList', props),
    },
    {
      id: TABS_IDS.purchaseListTab,
      title: i18n.t('entity.warehouse.labels.purchaseList'),
      content: (
        <MaterialPurchaseListTab
          warehouseId={warehouseId}
          onAddMaterialFromPurchaseListToBasket={props.onAddMaterialFromPurchaseListToBasket}
          onActiveFolderIdChange={setActiveFolderId}
          onFilterChange={handleFiltersChange}
          data-testid={suffixTestId('tabs.warehouse', props)}
        />
      ),
      'data-testid': suffixTestId('tab.purchaseList', props),
    },
  ];

  const activeTabContent = tabs.find(
    (tab) => tab.id === (activeTabId ?? TABS_IDS.warehouseTab)
  )?.content;

  return (
    <DataStatus isLoading={isSupplierOrderLoading || isSupplierOrderVariantLoading}>
      <VStack spacing={4} height="100%">
        <HStack justify="space-between">
          <TabsHeader
            variant="condensed"
            tabs={tabs}
            activeTabId={activeTabId}
            onChange={setActiveTabId}
            data-testid={suffixTestId('tabs', props)}
          />
          <Show when={activeTabId === TABS_IDS.warehouseTab}>
            <Tooltip
              isDisabled={articleCreate}
              label={i18n.t('module.warehouse.label.missingArticleCreatePermission')}
            >
              <Button
                variant="secondary"
                leftIcon="content/add_circle"
                onClick={handleCreateArticle}
                title={i18n.t('entity.warehouse.labels.createNewArticle')}
                isDisabled={!articleCreate}
                data-testid={suffixTestId('button.createNewArticle', props)}
              />
            </Tooltip>
          </Show>
        </HStack>
        <Card key={activeTabId} isFullHeight>
          <Box height="100%" flex={1}>
            {activeTabContent}
          </Box>
        </Card>
      </VStack>
    </DataStatus>
  );
}
